import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Avatar, IconButton, Stack, Button, Paper, Grid, Chip,
  Divider, MenuItem, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Breadcrumbs, Link, CircularProgress, Snackbar, Alert, Tooltip,
  CssBaseline
} from '@mui/material';
import {
  Add, DeleteOutline, Save, Edit, InfoOutlined,
  WarningAmber, ReceiptLong, CalendarMonth
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { pdf } from "@react-pdf/renderer";
import { saveAs } from 'file-saver';
import PayslipPDF from './Payslip';

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  filterBg: "#FFF9E6",
  accent: '#FFC107',
  accentDark: '#FFA000',
  errorRed: '#FFEBEE',
  errorText: '#C62828',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037'
};

export default function TutorAttendanceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- Logic State ---
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [selectedClassList, setSelectedClassList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  // --- UI State ---
  const [selectedDayData, setSelectedDayData] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null });

  // Forms
  const [newClassDetail, setNewClassDetail] = useState({
    courseName: "", courseId: "", time: "", payment: "", remarks: "Admin manual add"
  });
  const [editingLog, setEditingLog] = useState({ index: null, data: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    getAttendanceDetail();
    localStorage.setItem("current_tab", "Attendance");
  }, [id]);

  const getAttendanceDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/tutor-attendance/${id}`);
      const res = await response.json();
      if (res.data) {
        setAttendanceDetail(res.data);
        setSelectedClassList(res.data.classList || []);
        setSelectedStatus(res.data.status || "Pending");
        getCourseList(res.data.tutorId);
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error loading attendance data", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getCourseList = async (tutorId) => {
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff/${tutorId}/courses`);
      const res = await response.json();
      if (res.status === 1) {
        setCourseList([...res.data, { courseName: "Others", courseId: "others", time: "", payment: "" }]);
      }
    } catch (error) { console.error(error); }
  };

  const handleSaveAttendance = async () => {
    setLoadingSubmit(true);
    try {
      const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/tutor-attendance/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: selectedStatus, classList: selectedClassList })
      });
      const res = await response.json();
      if (res.status === 1) {
        setSnackbar({ open: true, message: "Attendance saved successfully!", severity: "success" });
        setIsDirty(false);
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDownloadPayslip = async () => {
    if (isDirty) return alert("Save changes before printing.");
    const blob = await pdf(<PayslipPDF data={attendanceDetail} />).toBlob();
    saveAs(blob, `${attendanceDetail?.tutorName} (Payslip ${attendanceDetail?.month} ${attendanceDetail?.year}).pdf`);
  };

  const handleSaveEdit = () => {
    if (!editingLog.data.courseName || !editingLog.data.time || Number(editingLog.data.payment) <= 0) {
      alert("Please fill in all required fields and ensure payment is valid.");
      return;
    }
    const updatedList = selectedClassList.map((item, idx) => idx === editingLog.index ? editingLog.data : item);
    setSelectedClassList(updatedList);
    setIsDirty(true);
    setOpenEditModal(false);
    if (selectedDayData) {
      setSelectedDayData({ ...selectedDayData, classes: updatedList.filter(c => c.date === selectedDayData.dateStr) });
    }
  };

  const handleAddClass = () => {
    if (!newClassDetail.courseName || !newClassDetail.time || Number(newClassDetail.payment) <= 0) {
      alert("Please fill in all required fields.");
      return;
    }
    const updatedList = [...selectedClassList, { ...newClassDetail, date: selectedDayData.dateStr, checkinTime: moment().format("HH:mm:ss") }];
    setSelectedClassList(updatedList);
    setIsDirty(true);
    setOpenAddModal(false);
    setSelectedDayData({ ...selectedDayData, classes: updatedList.filter(c => c.date === selectedDayData.dateStr) });
    setNewClassDetail({ courseName: "", courseId: "", time: "", payment: "", remarks: "Admin manual add" });
  };

  const confirmDeleteClass = () => {
    const newList = selectedClassList.filter((_, i) => i !== deleteConfirm.index);
    setSelectedClassList(newList);
    setIsDirty(true);
    setDeleteConfirm({ open: false, index: null });
    if (selectedDayData) {
      setSelectedDayData({ ...selectedDayData, classes: newList.filter(c => c.date === selectedDayData.dateStr) });
    }
  };

  const isLate = (scheduled, actual) => {
    if (!actual || actual === "-" || !scheduled || scheduled === "-") return false;
    const sTime = moment(scheduled, ["h:mm a", "HH:mm"]);
    const aTime = moment(actual, ["HH:mm:ss", "HH:mm"]);
    return aTime.diff(sTime, 'minutes') > 15;
  };

  const currentMonth = (attendanceDetail?.month && attendanceDetail?.year)
    ? moment(`${attendanceDetail.month} ${attendanceDetail.year}`, "MMMM YYYY") : moment();
  const firstDayIndex = currentMonth.isValid() ? currentMonth.startOf('month').day() : 0;
  const daysInMonth = currentMonth.isValid() ? currentMonth.daysInMonth() : 0;

  const totalAmount = selectedClassList.reduce((sum, item) => sum + Number(item.payment || 0), 0);
  const dailyTotal = selectedDayData?.classes.reduce((sum, item) => sum + Number(item.payment || 0), 0) || 0;

  if (loading || !attendanceDetail) return <Box display="flex" justifyContent="center" alignItems="center" height="100vh" sx={{ bgcolor: colors.sidebarBg }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

        <Breadcrumbs sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
          <Link underline="hover" color="inherit" onClick={() => navigate("/Portal/TutorAttendance")} sx={{ cursor: 'pointer' }}>Attendance</Link>
          <Typography color="text.primary">Attendance Details</Typography>
        </Breadcrumbs>

        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: colors.textMain }}>Attendance Details</Typography>

        {/* --- Header Info Bar --- */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, border: isDirty ? `2px solid ${colors.accent}` : 'none' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
            <Stack direction="row" spacing={4} alignItems="center">
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Tutor</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{attendanceDetail.tutorName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Period</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{attendanceDetail.month} {attendanceDetail.year}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary" display="block">Status</Typography>
                <TextField select size="small" value={selectedStatus} onChange={(e) => { setSelectedStatus(e.target.value); setIsDirty(true); }} sx={{ mt: 0.5, minWidth: 130, bgcolor: 'white' }}>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                </TextField>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box textAlign="right" mr={2}>
                <Typography variant="caption" color="textSecondary">Total RM</Typography>
                <Typography variant="h5" fontWeight="bold" color={colors.accent}>RM{totalAmount}</Typography>
              </Box>
              <Button variant="outlined" color="inherit" onClick={handleDownloadPayslip} startIcon={<ReceiptLong />} sx={{ borderRadius: 2 }}>Print</Button>
              <Button variant="contained" startIcon={loadingSubmit ? <CircularProgress size={18} /> : <Save />} onClick={handleSaveAttendance} disabled={loadingSubmit} sx={{ borderRadius: 2, bgcolor: isDirty ? colors.accent : 'gray', color: isDirty ? 'black' : 'white', '&:hover': { bgcolor: isDirty ? colors.accentDark : '#333' } }}>
                {loadingSubmit ? "Saving..." : isDirty ? "Save Changes*" : "Save Logs"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* --- Calendar Section --- */}
          <Grid item xs={12} md={8.5}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #F0E6CC' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <Typography key={d} align="center" variant="caption" fontWeight="bold" color="textSecondary" sx={{ pb: 1 }}>{d}</Typography>
                ))}
                {[...Array(firstDayIndex)].map((_, i) => <Box key={`e-${i}`} />)}
                {[...Array(daysInMonth)].map((_, i) => {
                  const dayNum = i + 1;
                  const dateStr = `${dayNum.toString().padStart(2, '0')}/${currentMonth.format("MM/YYYY")}`;
                  const dailyClasses = selectedClassList.filter(c => c.date === dateStr);
                  const dayRM = dailyClasses.reduce((sum, item) => sum + Number(item.payment || 0), 0);
                  const isSelected = selectedDayData?.date === dayNum;

                  return (
                    <Paper key={dayNum} elevation={0} onClick={() => setSelectedDayData({ date: dayNum, dateStr, classes: dailyClasses })}
                      sx={{
                        minHeight: 110, p: 0.8, border: isSelected ? `2px solid ${colors.accent}` : '1px solid #F0F0F0',
                        borderRadius: 2, cursor: 'pointer', transition: '0.2s', overflow: 'hidden', position: 'relative',
                        '&:hover': { bgcolor: colors.tableHeader }
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">{dayNum}</Typography>
                      <Stack spacing={0.4} mt={0.5}>
                        {dailyClasses.slice(0, 2).map((c, idx) => {
                          const late = isLate(c.time, c.checkinTime);
                          return (
                            <Box key={idx} sx={{ bgcolor: late ? colors.errorRed : '#E8EDF9', px: 0.5, py: 0.2, borderRadius: 0.5, borderLeft: `2px solid ${late ? colors.errorText : '#2196F3'}` }}>
                              <Typography sx={{ fontSize: '8.5px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: late ? colors.errorText : 'inherit' }}>
                                {c.courseName}
                              </Typography>
                            </Box>
                          );
                        })}
                        {dailyClasses.length > 2 && <Typography variant="caption" sx={{ fontSize: '8px', fontWeight: 'bold', color: 'text.secondary' }}>+{dailyClasses.length - 2} more..</Typography>}
                      </Stack>
                      {dayRM > 0 && (
                        <Typography variant="caption" sx={{ position: 'absolute', bottom: 2, right: 4, fontSize: '9px', fontWeight: 'bold', color: colors.textMain }}>RM{dayRM}</Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          </Grid>

          {/* --- Daily Detail Log Section --- */}
          <Grid item xs={12} md={3.5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, bgcolor: colors.pageBg, height: '100%', minHeight: 500, border: '1px solid #F0E6CC' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">Day Log</Typography>
                <IconButton size="small" onClick={() => selectedDayData ? setOpenAddModal(true) : alert("Select a date from calendar.")} sx={{ bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}>
                  <Add fontSize="small" />
                </IconButton>
              </Stack>

              {selectedDayData ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip label={selectedDayData.dateStr} sx={{ fontWeight: 'bold', bgcolor: 'white', border: '1px solid #DDD', mb: 1 }} />
                    <Typography variant="body2" color="textSecondary" fontWeight="bold">Daily Total: RM{dailyTotal}</Typography>
                  </Box>
                  <Divider />
                  {selectedDayData.classes.length > 0 ? (
                    selectedDayData.classes.map((cls, idx) => {
                      const fullIdx = selectedClassList.indexOf(cls);
                      const late = isLate(cls.time, cls.checkinTime);
                      return (
                        <Paper key={idx} sx={{ p: 2, borderRadius: 3, border: late ? `1px solid ${colors.errorText}` : 'none', position: 'relative', cursor: 'pointer', '&:hover': { bgcolor: colors.tableHeader } }} onClick={() => { setEditingLog({ index: fullIdx, data: { ...cls } }); setOpenEditModal(true); }}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ pr: 5 }}>{cls.courseName}</Typography>
                            <Typography variant="caption">Time: {cls.time} | RM{cls.payment}</Typography>
                            {late && <Typography sx={{ fontSize: '10px', color: colors.errorText, fontWeight: 'bold' }}>In: {cls.checkinTime} (LATE)</Typography>}
                          </Stack>
                          <Stack direction="row" sx={{ position: 'absolute', top: 5, right: 5 }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingLog({ index: fullIdx, data: { ...cls } }); setOpenEditModal(true); }}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, index: fullIdx }); }}><DeleteOutline fontSize="small" color="error" /></IconButton>
                          </Stack>
                        </Paper>
                      );
                    })
                  ) : <Typography variant="body2" color="textSecondary" align="center">No logs found.</Typography>}
                </Stack>
              ) : (
                <Box textAlign="center" mt={10}>
                  <CalendarMonth sx={{ fontSize: 40, color: '#CCC', mb: 1 }} />
                  <Typography variant="body2" color="textSecondary">Select a date from calendar.</Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* --- Dialogs maintained with consistent styling --- */}
        <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} PaperProps={{ sx: { borderRadius: 4, width: 450 } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Add Entry: {selectedDayData?.dateStr}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'red' }}>* Required fields</Typography>
              <TextField select fullWidth size="small" label="Select Class *" value={newClassDetail.courseName}
                onChange={(e) => {
                  const course = courseList.find(c => c.courseName === e.target.value);
                  setNewClassDetail({ ...newClassDetail, courseName: course.courseName, courseId: course.courseId, time: course.time || "", payment: course.payment || "" });
                }}
              >
                {courseList.map((c, i) => <MenuItem key={i} value={c.courseName}>{c.courseName}</MenuItem>)}
              </TextField>
              {newClassDetail.courseId !== "" && (
                <Stack spacing={2}>
                  {newClassDetail.courseId === "others" && (
                    <TextField fullWidth size="small" label="Manual Class Name *" onChange={(e) => setNewClassDetail({ ...newClassDetail, courseName: e.target.value })} />
                  )}
                  <Stack direction="row" spacing={2}>
                    <TextField label="Time *" type="time" fullWidth size="small" value={newClassDetail.time ? moment(newClassDetail.time, "hh:mm a").format("HH:mm") : ""} onChange={(e) => setNewClassDetail({ ...newClassDetail, time: moment(e.target.value, "HH:mm").format("hh:mm a") })} InputLabelProps={{ shrink: true }} />
                    <TextField label="Payment (RM) *" fullWidth size="small" type="number" value={newClassDetail.payment} onChange={(e) => setNewClassDetail({ ...newClassDetail, payment: e.target.value })} />
                  </Stack>
                </Stack>
              )}
              <TextField fullWidth size="small" label="Remarks (Optional)" multiline rows={2} value={newClassDetail.remarks} onChange={(e) => setNewClassDetail({ ...newClassDetail, remarks: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenAddModal(false)} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={handleAddClass} sx={{ bgcolor: colors.accent, color: 'black', '&:hover': { bgcolor: colors.accentDark } }}>Confirm</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} PaperProps={{ sx: { borderRadius: 4, width: 450 } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Attendance Entry</DialogTitle>
          <DialogContent>
            {editingLog.data && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField label="Class Name *" fullWidth size="small" value={editingLog.data.courseName} onChange={(e) => setEditingLog({ ...editingLog, data: { ...editingLog.data, courseName: e.target.value } })} />
                <Stack direction="row" spacing={2}>
                  <TextField label="Time *" type="time" fullWidth size="small" value={editingLog.data.time ? moment(editingLog.data.time, "hh:mm a").format("HH:mm") : ""} onChange={(e) => setEditingLog({ ...editingLog, data: { ...editingLog.data, time: moment(e.target.value, "HH:mm").format("hh:mm a") } })} InputLabelProps={{ shrink: true }} />
                  <TextField label="Payment (RM) *" fullWidth size="small" type="number" value={editingLog.data.payment} onChange={(e) => setEditingLog({ ...editingLog, data: { ...editingLog.data, payment: e.target.value } })} />
                </Stack>
                <TextField label="Remarks (Optional)" fullWidth size="small" multiline rows={2} value={editingLog.data.remarks} onChange={(e) => setEditingLog({ ...editingLog, data: { ...editingLog.data, remarks: e.target.value } })} />
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setOpenEditModal(false)} color="inherit">Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} sx={{ bgcolor: colors.accent, color: 'black', '&:hover': { bgcolor: colors.accentDark } }}>Save Changes</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, index: null })} PaperProps={{ sx: { borderRadius: 4, width: 350 } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: colors.errorText }}><WarningAmber /> Confirm Delete</DialogTitle>
          <DialogContent><Typography variant="body2">Remove this log from {selectedDayData?.dateStr}?</Typography></DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteConfirm({ open: false, index: null })} color="inherit">Cancel</Button>
            <Button onClick={confirmDeleteClass} variant="contained" sx={{ bgcolor: colors.errorText, color: 'white' }}>Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}><Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert></Snackbar>
      </Box>
    </Box>
  );
}